import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { initialTheme, pageContents, siteMapNodes, type SiteNode, type ThemeData, type PageContent } from '../../../mocks/websiteBuilder';

export const STEP_ROUTES: Record<number, string> = {
  1: '/website-builder/sitemap',
  2: '/website-builder/editor',
  3: '/website-builder/design',
  4: '/website-builder/export',
};

export const ROUTE_STEPS: Record<string, number> = {
  '/website-builder/sitemap': 1,
  '/website-builder/editor': 2,
  '/website-builder/design': 3,
  '/website-builder/export': 4,
};
import api from '../../../services/api';
import { ACTIONS } from '../../../telemetry/constants/actions';
import { CREDIT_COSTS, CREDIT_THRESHOLDS } from '../../../config/creditCosts';
import { creditService } from '../../../services/creditService';
import { useCredits } from '../../../hooks/useCredits';
import { usePlatformNotifications } from '../../../hooks/usePlatformNotifications';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { MODULES } from '../../../telemetry/constants/modules';

// ─── Types ────────────────────────────────────────────────────────────────────

export type GlobalContactInfo = {
  email: string;
  phone: string;
  location: string;
  description: string;
  socials: Record<string, string>;
};

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'unsaved';
export type ProjectState = 'None' | 'Draft' | 'Downloaded' | 'checking';

export interface WebsiteBuilderContextValue {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  handleStepChange: (step: number) => Promise<void>;
  handleNext: () => Promise<void>;
  handleBack: () => void;
  pages: PageContent[];
  setPages: React.Dispatch<React.SetStateAction<PageContent[]>>;
  nodes: SiteNode[];
  setNodes: React.Dispatch<React.SetStateAction<SiteNode[]>>;
  activePageId: string;
  setActivePageId: React.Dispatch<React.SetStateAction<string>>;
  brandName: string;
  setBrandName: React.Dispatch<React.SetStateAction<string>>;
  brandLogo: string | null;
  setBrandLogo: React.Dispatch<React.SetStateAction<string | null>>;
  brandTheme: Partial<ThemeData> | null;
  setBrandTheme: React.Dispatch<React.SetStateAction<Partial<ThemeData> | null>>;
  theme: ThemeData;
  setTheme: React.Dispatch<React.SetStateAction<ThemeData>>;
  selectedTemplate: string;
  setSelectedTemplate: React.Dispatch<React.SetStateAction<string>>;
  globalContactInfo: GlobalContactInfo;
  setGlobalContactInfo: React.Dispatch<React.SetStateAction<GlobalContactInfo>>;
  projectState: ProjectState;
  setProjectState: React.Dispatch<React.SetStateAction<ProjectState>>;
  isExported: boolean;
  setIsExported: React.Dispatch<React.SetStateAction<boolean>>;
  isCheckingSession: boolean;
  showSessionModal: boolean;
  setShowSessionModal: React.Dispatch<React.SetStateAction<boolean>>;
  showLockedModal: boolean;
  setShowLockedModal: React.Dispatch<React.SetStateAction<boolean>>;
  showNoSessionModal: boolean;
  setShowNoSessionModal: React.Dispatch<React.SetStateAction<boolean>>;
  globalLoading: boolean;
  loadingText: string;
  saveStatus: SaveStatus;
  lastSavedAt: Date | null;
  handleManualSave: (overridePages?: PageContent[], overrideContact?: GlobalContactInfo) => Promise<void>;
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modalCredits: number;
  modalRequired: number;
  modalIsBlocker: boolean;
  showDeductionConfirmModal: boolean;
  setShowDeductionConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
  isDeducting: boolean;
  triggerChargeConfirm: (action: 'start-fresh' | 'new-site' | 'unlock') => Promise<void>;
  handleConfirmDeduction: () => Promise<void>;
  showPreview: boolean;
  setShowPreview: React.Dispatch<React.SetStateAction<boolean>>;
  handleStartFresh: () => Promise<void>;
  handleRestoreSession: (isSilent?: boolean) => Promise<void>;
  isSessionReady: boolean;
  processStep1To2: () => Promise<void>;
  deduplicatePages: (list: PageContent[]) => PageContent[];
  isOnboardingCompleted: boolean | null;
  setIsOnboardingCompleted: React.Dispatch<React.SetStateAction<boolean | null>>;
  checkOnboardingStatus: () => Promise<boolean>;
}

const WebsiteBuilderContext = createContext<WebsiteBuilderContextValue | null>(null);

export function useWebsiteBuilder(): WebsiteBuilderContextValue {
  const ctx = useContext(WebsiteBuilderContext);
  if (!ctx) throw new Error('useWebsiteBuilder must be used inside <WebsiteBuilderProvider>');
  return ctx;
}

export function WebsiteBuilderProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { invalidateCredits } = useCredits();
  const { notificationsQuery, unreadCountQuery } = usePlatformNotifications();
  const { trackAction } = useTelemetry({ module: MODULES.WEBSITE_BUILDER, submodule: 'Website Builder' });

  const [currentStep, setCurrentStep] = useState(() => ROUTE_STEPS[location.pathname] || 1);
  const [isOnboardingCompleted, setIsOnboardingCompleted] = useState<boolean | null>(null);

  const checkOnboardingStatus = useCallback(async () => {
    try {
      const res = await api.getWbOnboardingStatus();
      const completed = res?.data?.data?.is_onboarding_completed ?? res?.data?.is_onboarding_completed ?? false;
      setIsOnboardingCompleted(completed);
      return completed;
    } catch (err) {
      console.error('[WebsiteBuilder] Failed to check onboarding status:', err);
      setIsOnboardingCompleted(false);
      return false;
    }
  }, []);

  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  // Sync currentStep when route changes (e.g. browser back/forward)
  useEffect(() => {
    const step = ROUTE_STEPS[location.pathname];
    if (step && step !== currentStep) {
      setCurrentStep(step);
    }
  }, [location.pathname, currentStep]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCredits, setModalCredits] = useState(0);
  const [modalRequired, setModalRequired] = useState(0);
  const [modalIsBlocker, setModalIsBlocker] = useState(false);
  const [projectState, setProjectState] = useState<ProjectState>('checking');
  const [isExported, setIsExported] = useState(false);
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [showNoSessionModal, setShowNoSessionModal] = useState(false);
  const [showDeductionConfirmModal, setShowDeductionConfirmModal] = useState(false);
  const [isDeducting, setIsDeducting] = useState(false);
  const [pendingAction, setPendingAction] = useState<'new-site' | 'start-fresh' | 'unlock' | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('t1');
  const [theme, setTheme] = useState<ThemeData>(initialTheme);
  const [brandTheme, setBrandTheme] = useState<Partial<ThemeData> | null>(null);
  const [pages, setPages] = useState<PageContent[]>(pageContents);
  const [activePageId, setActivePageId] = useState<string>('home');
  const initialBrandName = () => {
    const stateBrand = (location.state as { brand?: string })?.brand;
    if (stateBrand && stateBrand.trim()) return stateBrand;
    const stored = localStorage.getItem('brand_name') || localStorage.getItem('business_name');
    if (stored && stored.trim()) return stored;
    return '';
  };
  const initialBrandLogo = () => {
    const storedLogo = localStorage.getItem('logo_url');
    if (storedLogo) return storedLogo.replace(/"/g, '');
    return null;
  };

  const [brandName, setBrandName] = useState<string>(initialBrandName);
  const [brandLogo, setBrandLogo] = useState<string | null>(initialBrandLogo);
  const [globalContactInfo, setGlobalContactInfo] = useState<GlobalContactInfo>({ email: '', phone: '', location: '', description: '', socials: {} });
  const [globalLoading, setGlobalLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const hasInitializedSessionRef = useRef(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  const latestPagesRef = useRef(pages);
  const latestContactRef = useRef(globalContactInfo);

  const [nodes, setNodes] = useState<SiteNode[]>(() => {
    const state = location.state as { brand?: string; businessType?: string; offeringType?: string };
    const offeringType = state?.offeringType?.toLowerCase() || 'both';
    let initialNodes = JSON.parse(JSON.stringify(siteMapNodes));
    if (offeringType === 'product') initialNodes = initialNodes.filter((n: SiteNode) => n.id !== 'services');
    else if (offeringType === 'service') initialNodes = initialNodes.filter((n: SiteNode) => n.id !== 'products');
    initialNodes = initialNodes.map((node: SiteNode) => {
      if (node.id === 'services' || node.id === 'products') delete node.children;
      return node;
    });
    return initialNodes;
  });

  useEffect(() => { latestPagesRef.current = pages; }, [pages]);
  useEffect(() => { latestContactRef.current = globalContactInfo; }, [globalContactInfo]);

  const deduplicatePages = useCallback((pagesList: PageContent[]): PageContent[] => {
    if (!Array.isArray(pagesList)) return [];
    const map = new Map<string, PageContent>();
    for (const page of pagesList) {
      let cleanId = page.pageId;
      if (cleanId === 'about-us') cleanId = 'about';
      if (cleanId === 'contact-us') cleanId = 'contact';
      if (cleanId === 'service-landing') cleanId = 'services';
      if (cleanId === 'product-landing') cleanId = 'products';
      let cleanName = page.pageName;
      if (cleanId === 'about') cleanName = 'About Us';
      if (cleanId === 'contact') cleanName = 'Contact Us';
      if (cleanId === 'services') cleanName = 'Services';
      if (cleanId === 'products') cleanName = 'Products';
      if (cleanId === 'home') cleanName = 'Home';
      if (cleanId === 'blog') cleanName = 'Blog';
      if (cleanId === 'gallery') cleanName = 'Gallery';
      const normalizedPage: PageContent = { ...page, pageId: cleanId, pageName: cleanName };
      if (map.has(cleanId)) {
        const existing = map.get(cleanId)!;
        if (normalizedPage.status === 'Generated' && existing.status !== 'Generated') map.set(cleanId, normalizedPage);
      } else {
        map.set(cleanId, normalizedPage);
      }
    }
    return Array.from(map.values());
  }, []);

  const checkCreditsForAction = useCallback(async (required: number): Promise<{ allowed: boolean; current: number }> => {
    try {
      const bal = await creditService.getBalance();
      const current = bal.total_credits_balance;
      if (current < required) { setModalCredits(current); setModalRequired(required); setModalIsBlocker(true); setModalOpen(true); return { allowed: false, current }; }
      return { allowed: true, current };
    } catch (err) { console.error('Failed to check credits:', err); }
    return { allowed: true, current: 0 };
  }, []);

  const checkLowCreditWarning = useCallback(async (current: number) => {
    if (current < CREDIT_THRESHOLDS.LOW_CREDIT_WARNING) { setModalCredits(current); setModalIsBlocker(false); setModalOpen(true); }
  }, []);

  const triggerChargeConfirm = useCallback(async (action: 'start-fresh' | 'new-site' | 'unlock') => {
    try {
      const bal = await creditService.getBalance();
      const current = bal.total_credits_balance;
      const required = CREDIT_COSTS.FULL_WEBSITE || 5000;
      if (current < required) { setModalCredits(current); setModalRequired(required); setModalIsBlocker(true); setModalOpen(true); return; }
      setModalCredits(current); setPendingAction(action); setShowDeductionConfirmModal(true);
    } catch (err) { console.error('Failed to check credits for charge:', err); }
  }, []);

  const handleConfirmDeduction = useCallback(async () => {
    setIsDeducting(true);
    try {
      if (pendingAction === 'unlock') {
        const res = await api.unlockWebsiteBuilder();
        if (res.status === 'success') { setIsExported(false); setShowDeductionConfirmModal(false); await invalidateCredits(); notificationsQuery.refetch(); unreadCountQuery.refetch(); }
      } else {
        const res = await api.chargeWebsiteBuilder();
        if (res.status === 'success') { setShowDeductionConfirmModal(false); window.location.reload(); }
      }
    } catch (err) { console.error('Deduction failed:', err); alert('Failed to charge credits. Please try again.'); }
    finally { setIsDeducting(false); }
  }, [pendingAction, invalidateCredits, notificationsQuery, unreadCountQuery]);

  const getDerivedPages = useCallback((): PageContent[] => {
    const activeNodes = nodes.filter(n => n.enabled);
    const derived = activeNodes.map(n => {
      const existing = pages.find(p => p.pageId === n.id || p.pageName.toLowerCase() === n.name.toLowerCase())
                    || pageContents.find(p => p.pageId === n.id || p.pageName.toLowerCase() === n.name.toLowerCase());
      if (existing) {
        return { ...existing, pageId: n.id, pageName: n.name, sections: existing.sections.map(s => ({ ...s, content: Array.isArray(s.content) ? s.content : (s.content || '') })) };
      }
      const descLower = (n.description || '').toLowerCase().trim();
      const nameLower = (n.name || '').toLowerCase().trim();
      const combined = `${nameLower} ${descLower}`;
      const customSections: any[] = [{ id: `hero_${n.id}`, type: 'hero', title: 'Hero Banner', enabled: true, content: [] }];
      if (descLower) {
        if (/career|job|opening|hiring|recruit|culture|employee|growth opportunit/.test(combined)) {
          if (/culture|benefit|perk|why us|growth|value/.test(combined)) customSections.push({ id: `usp_${n.id}`, type: 'usp', title: 'Culture & Benefits', enabled: true, content: [] });
          if (/job|opening|position|role|vacancy|hiring|opportunit/.test(combined)) customSections.push({ id: `grid_${n.id}`, type: 'grid', title: 'Current Openings', enabled: true, content: [] });
          if (/culture|story|vision|environment|workplace|life at/.test(combined)) customSections.push({ id: `story_${n.id}`, type: 'story', title: 'Life at Our Company', enabled: true, content: '' });
          if (/apply|application|join|process|contact|submit|resume|cv/.test(combined)) customSections.push({ id: `contactForm_${n.id}`, type: 'contactForm', title: 'Application Form', enabled: true, content: '' });
          if (customSections.length === 1) {
            customSections.push(
              { id: `usp_${n.id}`, type: 'usp', title: 'Culture & Benefits', enabled: true, content: [] },
              { id: `grid_${n.id}`, type: 'grid', title: 'Current Openings', enabled: true, content: [] },
              { id: `story_${n.id}`, type: 'story', title: 'About Working Here', enabled: true, content: '' },
              { id: `contactForm_${n.id}`, type: 'contactForm', title: 'Apply Now', enabled: true, content: '' },
            );
          }
        } else if (/price|pricing|plan|tier|subscription|cost|fee|package/.test(combined)) {
          customSections.push({ id: `grid_${n.id}`, type: 'grid', title: 'Pricing Plans', enabled: true, content: [] });
          customSections.push({ id: `faq_${n.id}`, type: 'faq', title: 'Frequently Asked Questions', enabled: true, content: [] });
        } else if (/team|board|leadership|staff|people|executiv|founder/.test(combined)) {
          customSections.push({ id: `team_${n.id}`, type: 'team', title: 'Our Team', enabled: true, content: [] });
          customSections.push({ id: `story_${n.id}`, type: 'story', title: 'About Our Team', enabled: true, content: '' });
        } else if (/faq|question|help|support|guide/.test(combined)) {
          customSections.push({ id: `faq_${n.id}`, type: 'faq', title: 'FAQ', enabled: true, content: [] });
          customSections.push({ id: `contactForm_${n.id}`, type: 'contactForm', title: 'Get Help', enabled: true, content: '' });
        } else {
          customSections.push({ id: `story_${n.id}`, type: 'story', title: 'Main Content', enabled: true, content: '' });
          customSections.push({ id: `grid_${n.id}`, type: 'grid', title: 'Highlights', enabled: true, content: [] });
        }
      } else {
        customSections.push({ id: `story_${n.id}`, type: 'story', title: 'Main Content', enabled: true, content: '' });
        if (n.id === 'contact') {
          customSections.push(
            { id: `contactInfo_${n.id}`, type: 'contactInfo', title: 'Contact Information', enabled: true, content: JSON.stringify({ description: 'Reach out to us directly' }) },
            { id: `contactForm_${n.id}`, type: 'contactForm', title: 'Contact Form', enabled: true, content: JSON.stringify({ heading: 'Send a Message', subtext: 'We will respond within 24 hours' }) },
          );
        } else if (n.id === 'blog') {
          customSections.push({ id: `grid_${n.id}`, type: 'grid', title: 'Blog Posts', enabled: true, content: [] });
        } else if (n.id === 'gallery') {
          customSections.push({ id: `gallery_${n.id}`, type: 'gallery', title: 'Gallery', enabled: true, content: [] });
        }
      }
      return { pageId: n.id, pageName: n.name, status: 'Pending' as const, sections: customSections };
    }) as PageContent[];
    return deduplicatePages(derived);
  }, [nodes, pages, deduplicatePages]);

  const processStep1To2 = useCallback(async () => {
    if (projectState === 'None') {
      const checkRes = await checkCreditsForAction(CREDIT_COSTS.FULL_WEBSITE);
      if (!checkRes.allowed) return;
    }
    try {
      setGlobalLoading(true); setIsExported(false); setProjectState('Draft');
      setLoadingText('Initializing project structure...');
      trackAction(ACTIONS.GENERATE_WEBSITE, 'Initialize Website Project', { nodes_count: nodes.length });
      await api.initializeWebsite(nodes);
      setLoadingText('Preparing content canvas...');
      const apiRes = await api.getPages();
      const truePages = deduplicatePages(apiRes.pages || []);
      if (apiRes.globalContactInfo) setGlobalContactInfo(prev => ({ ...prev, ...apiRes.globalContactInfo }));
      const activeNodes = nodes.filter(n => n.enabled);
      const orderMap = new Map(activeNodes.map((n, idx) => [n.id, idx]));
      const sortedTruePages = Array.isArray(truePages) && truePages.length > 0
        ? [...truePages].sort((a, b) => (orderMap.get(a.pageId) ?? 999) - (orderMap.get(b.pageId) ?? 999))
        : [];
      setPages(deduplicatePages(sortedTruePages.length > 0 ? sortedTruePages : getDerivedPages()));
      setCurrentStep(2);
      navigate(STEP_ROUTES[2]);
      try { const bal = await creditService.getBalance(); await checkLowCreditWarning(bal.total_credits_balance); } catch (err) { console.error('Failed to check warning:', err); }
    } catch (err) {
      console.error('Initialization failed, falling back to local generation', err);
      setPages(deduplicatePages(getDerivedPages()));
      setCurrentStep(2);
      navigate(STEP_ROUTES[2]);
    } finally { setGlobalLoading(false); }
  }, [projectState, nodes, checkCreditsForAction, deduplicatePages, getDerivedPages, checkLowCreditWarning, trackAction, navigate]);

  const handleStartFresh = useCallback(async () => {
    if (isExported) { setShowSessionModal(false); triggerChargeConfirm('start-fresh'); return; }
    setGlobalLoading(true); setLoadingText('Starting fresh...');
    try {
      await api.startFreshWebsite();
      setShowSessionModal(false);
      setCurrentStep(1);
      navigate(STEP_ROUTES[1]);
    }
    catch (err) { console.error('Start fresh failed:', err); }
    finally { setGlobalLoading(false); }
  }, [isExported, triggerChargeConfirm, navigate]);

  const handleRestoreSession = useCallback(async (isSilent = false) => {
    if (!isSilent) {
      setGlobalLoading(true);
      setLoadingText('Restoring your session...');
    }
    try {
      const apiRes = await api.getPages();
      const fetchedPages = deduplicatePages(apiRes.pages || []);
      if (apiRes.globalContactInfo) setGlobalContactInfo(prev => ({ ...prev, ...apiRes.globalContactInfo }));
      const patchedPages = fetchedPages.map((p: PageContent) => {
        let sections = [...p.sections];
        if (p.pageId === 'contact') {
          if (!sections.some(s => s.type === 'contactInfo')) sections.push({ id: `contactInfo_${p.pageId}`, type: 'contactInfo', title: 'Contact Information', enabled: true, content: JSON.stringify({ description: 'Reach out to us directly' }), editing: false });
          if (!sections.some(s => s.type === 'contactForm')) sections.push({ id: `contactForm_${p.pageId}`, type: 'contactForm', title: 'Contact Form', enabled: true, content: JSON.stringify({ heading: 'Send a Message', subtext: 'We will respond within 24 hours' }), editing: false });
        } else {
          sections = sections.filter(s => s.type !== 'contactInfo' && s.type !== 'contactForm');
        }
        return { ...p, sections };
      });
      const finalDedupedPages = deduplicatePages(patchedPages);
      setPages(finalDedupedPages);
      if (finalDedupedPages.length > 0) {
        setNodes(prev => {
          const restoredIds = finalDedupedPages.map(p => p.pageId);
          const orderedNodes = finalDedupedPages.map(p => prev.find(n => n.id === p.pageId) || { id: p.pageId, name: p.pageName, description: 'Custom page', enabled: true, status: ['home', 'about', 'services', 'products', 'blog', 'gallery', 'contact'].includes(p.pageId) ? 'default' : 'optional', children: [] });
          const remaining = prev.filter(n => !restoredIds.includes(n.id)).map(n => ({ ...n, enabled: false }));
          return [...orderedNodes, ...remaining];
        });
      }
      if (projectState === 'Downloaded') {
        setIsExported(true);
        setCurrentStep(4);
        if (location.pathname !== '/website-builder/preview') {
          navigate(STEP_ROUTES[4]);
        }
      } else {
        setIsExported(false);
        setProjectState('Draft');
        setCurrentStep(2);
        if (location.pathname !== '/website-builder/preview') {
          navigate(STEP_ROUTES[2]);
        }
      }
      setShowSessionModal(false);
    } catch (err) { console.error('Restore session failed:', err); }
    finally {
      if (!isSilent) setGlobalLoading(false);
      setIsSessionReady(true);
    }
  }, [deduplicatePages, projectState, location.pathname, navigate]);

  const handleManualSave = useCallback(async (overridePages?: PageContent[], overrideContact?: GlobalContactInfo) => {
    const targetPages = overridePages || latestPagesRef.current;
    const targetContact = overrideContact || latestContactRef.current;
    const dedupedPages = deduplicatePages(targetPages);
    if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }
    setSaveStatus('saving');
    try {
      trackAction(ACTIONS.EDIT_WEBSITE, 'Save Website Content', { pages_count: dedupedPages.length });
      await api.saveContent(dedupedPages, targetContact);
      setSaveStatus('saved'); setLastSavedAt(new Date());
    } catch (err) { console.error('Save content failed:', err); setSaveStatus('unsaved'); }
  }, [deduplicatePages, trackAction]);

  const saveCurrentContent = useCallback(async () => {
    try { setGlobalLoading(true); setLoadingText('Saving your changes...'); await handleManualSave(latestPagesRef.current, latestContactRef.current); }
    catch (err) { console.error(err); }
    finally { setGlobalLoading(false); }
  }, [handleManualSave]);

  useEffect(() => {
    if (isInitialLoadRef.current) { isInitialLoadRef.current = false; return; }
    if (currentStep < 2) return;
    setSaveStatus('unsaved');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => { await handleManualSave(latestPagesRef.current, latestContactRef.current); }, 5000);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [pages, globalContactInfo, currentStep, isExported, handleManualSave]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => { if (saveStatus === 'unsaved' || saveStatus === 'saving') { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveStatus]);

  useEffect(() => {
    if (hasInitializedSessionRef.current) return;
    hasInitializedSessionRef.current = true;

    const fetchProfile = async () => {
      try {
        const userId = localStorage.getItem('user_id');
        if (!userId) return;
        const resJson = await api.getWebsiteProfile(userId);
        const data = resJson.data || resJson;
        const profileData = data.business_profile || data;
        const productsData = data.products || [];
        const servicesData = data.services || [];
        if (profileData) {
          if (profileData.business_name) setBrandName(profileData.business_name);
          if (profileData.logo_url) setBrandLogo(profileData.logo_url.replace(/"/g, ''));
          setGlobalContactInfo(prev => {
            const next = { ...prev };
            const isEncrypted = (v: string) => v && v.startsWith('gAAAAAB');
            if (profileData.email && !isEncrypted(profileData.email)) next.email = profileData.email;
            if (profileData.phone_number && !isEncrypted(profileData.phone_number)) next.phone = profileData.phone_number.replace(/-/g, '');
            if (profileData.location || profileData.city || profileData.state || profileData.country) {
              const parts = [profileData.location, profileData.city, profileData.state, profileData.country].filter((v: string) => v && !isEncrypted(v));
              if (parts.length > 0) next.location = parts.join(', ');
            }
            return next;
          });
          const toTitleCase = (str: string) => { if (!str) return ''; return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()); };
          const cleanArray = (arr: any[]) => { if (!Array.isArray(arr)) return []; return arr.map(item => { let val = ''; if (typeof item === 'string') val = item; else if (typeof item === 'object' && item !== null) { val = item.name || item.title || item.product_name || item.service_name || ''; } return toTitleCase(val.trim()); }).filter(item => item.length > 0); };
          const servicesList = cleanArray(servicesData);
          const productsList = cleanArray(productsData);
          const hasProducts = productsList.length > 0; const hasServices = servicesList.length > 0;
          let dynamicOfferingType = 'both';
          if (hasProducts && !hasServices) dynamicOfferingType = 'product';
          if (hasServices && !hasProducts) dynamicOfferingType = 'service';
          setNodes(() => {
            let initialNodes = JSON.parse(JSON.stringify(siteMapNodes));
            if (dynamicOfferingType === 'product') initialNodes = initialNodes.filter((n: SiteNode) => n.id !== 'services');
            else if (dynamicOfferingType === 'service') initialNodes = initialNodes.filter((n: SiteNode) => n.id !== 'products');
            initialNodes = initialNodes.map((node: SiteNode) => { if (node.id === 'services' || node.id === 'products') delete node.children; return node; });
            return initialNodes;
          });
          if (profileData.primary_color || profileData.secondary_color || profileData.primary_font || profileData.secondary_font) {
            const nextBrandTheme: Partial<ThemeData> = {};
            if (profileData.primary_color) { nextBrandTheme.primary = profileData.primary_color; nextBrandTheme.cta = profileData.primary_color; }
            if (profileData.secondary_color) nextBrandTheme.accent = profileData.secondary_color;
            if (profileData.primary_font) nextBrandTheme.headingFont = profileData.primary_font;
            if (profileData.secondary_font) nextBrandTheme.bodyFont = profileData.secondary_font;
            setBrandTheme(nextBrandTheme); setTheme(prev => ({ ...prev, ...nextBrandTheme }));
          }
        }
      } catch (err) { console.error('Failed to fetch onboarding profile:', err); }
    };
    const checkSession = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        const stateRes = await api.getWebsiteProjectState();
        const state = stateRes.data?.state;
        setProjectState(state || 'None');

        // Direct navigation to shareable preview page:
        if (location.pathname === '/website-builder/preview') {
          if (state === 'Draft' || state === 'Downloaded') {
            await handleRestoreSession(true);
          }
          return;
        }

        if (state === 'Downloaded') { setIsExported(true); setShowSessionModal(true); }
        else if (state === 'None') { setShowNoSessionModal(true); }
        else if (state === 'Draft') {
          const res = await api.checkWebsiteSession();
          if (res.status === 'success' && res.data?.session_exists) setShowSessionModal(true);
        }
      } catch (err) { console.error('Failed to check session:', err); }
      finally {
        setIsCheckingSession(false);
        setIsSessionReady(true);
      }
    };
    fetchProfile();
    checkSession();
  }, []);

  const handleStepChange = useCallback(async (step: number) => {
    if (isExported) {
      setCurrentStep(step);
      if (STEP_ROUTES[step]) navigate(STEP_ROUTES[step]);
      return;
    }
    if (step === 2 && currentStep === 1) {
      await processStep1To2();
    } else if (currentStep === 2 && step !== 2) {
      await saveCurrentContent();
      if (step <= currentStep + 1) {
        setCurrentStep(step);
        if (STEP_ROUTES[step]) navigate(STEP_ROUTES[step]);
      }
    } else if (step <= currentStep + 1) {
      setCurrentStep(step);
      if (STEP_ROUTES[step]) navigate(STEP_ROUTES[step]);
    }
  }, [isExported, currentStep, processStep1To2, saveCurrentContent, navigate]);

  const handleNext = useCallback(async () => {
    if (isExported) {
      if (currentStep < 4) {
        const next = currentStep + 1;
        setCurrentStep(next);
        if (STEP_ROUTES[next]) navigate(STEP_ROUTES[next]);
      }
      return;
    }
    if (currentStep === 1) {
      await processStep1To2();
    } else if (currentStep === 2) {
      await saveCurrentContent();
      setCurrentStep(3);
      navigate(STEP_ROUTES[3]);
    } else if (currentStep === 3) {
      setCurrentStep(4);
      navigate(STEP_ROUTES[4]);
    } else if (currentStep < 4) {
      const next = currentStep + 1;
      setCurrentStep(next);
      if (STEP_ROUTES[next]) navigate(STEP_ROUTES[next]);
    }
  }, [isExported, currentStep, processStep1To2, saveCurrentContent, navigate]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      if (STEP_ROUTES[prev]) navigate(STEP_ROUTES[prev]);
    }
  }, [currentStep, navigate]);

  const handleSetShowPreview: React.Dispatch<React.SetStateAction<boolean>> = useCallback((action) => {
    setShowPreview(prev => {
      const next = typeof action === 'function' ? action(prev) : action;
      if (next) {
        navigate('/website-builder/preview');
      }
      return next;
    });
  }, [navigate]);

  const value: WebsiteBuilderContextValue = {
    currentStep, setCurrentStep, handleStepChange, handleNext, handleBack,
    pages, setPages, nodes, setNodes, activePageId, setActivePageId,
    brandName, setBrandName, brandLogo, setBrandLogo, brandTheme, setBrandTheme,
    theme, setTheme, selectedTemplate, setSelectedTemplate,
    globalContactInfo, setGlobalContactInfo,
    projectState, setProjectState, isExported, setIsExported,
    isCheckingSession, isSessionReady, showSessionModal, setShowSessionModal,
    showLockedModal, setShowLockedModal, showNoSessionModal, setShowNoSessionModal,
    globalLoading, loadingText, saveStatus, lastSavedAt, handleManualSave,
    modalOpen, setModalOpen, modalCredits, modalRequired, modalIsBlocker,
    showDeductionConfirmModal, setShowDeductionConfirmModal, isDeducting,
    triggerChargeConfirm, handleConfirmDeduction,
    showPreview, setShowPreview: handleSetShowPreview,
    handleStartFresh, handleRestoreSession, processStep1To2, deduplicatePages,
    isOnboardingCompleted, setIsOnboardingCompleted, checkOnboardingStatus,
  };

  return <WebsiteBuilderContext.Provider value={value}>{children}</WebsiteBuilderContext.Provider>;
}
